import { createContext, use } from "react";
import type { JSX, ReactNode } from "react";

const PageBannersContext = createContext<ReactNode>(null);

/** Properties of {@link PageBannersProvider}. */
export interface PageBannersProviderProps {
  /** Banners shown on every page, such as the service status or a pending legal document. */
  readonly banners: ReactNode;
  /** Child elements. */
  readonly children: ReactNode;
}

/**
 * Provides the banners of the app shell to the page headers below it. The shell does not render
 * them above the content: {@link PageBanners} places them right after the page heading, so the
 * `h1` comes first and the main action of the page (such as „Kommen“) stays in view (UX-001).
 *
 * @param props - Component properties.
 * @returns The provider element.
 */
export function PageBannersProvider(props: PageBannersProviderProps): JSX.Element {
  const provider = <PageBannersContext value={props.banners}>{props.children}</PageBannersContext>;

  return provider;
}

/**
 * Renders the banners of the app shell at its position, without a wrapper, so a banner that has
 * nothing to say adds no space. Outside a {@link PageBannersProvider} (sign-in, tenant selection)
 * it renders nothing.
 *
 * @returns The banners.
 */
export function PageBanners(): JSX.Element {
  const banners = use(PageBannersContext);
  const element = <>{banners}</>;

  return element;
}
